import type { TabularConfigDraftDiff } from '@axilon-apps/domains-ontology-ui-state'

export type ChangedSection =
  | 'output'
  | 'pageTables'
  | 'checks'
  | 'description'
  | 'script'
  | 'agentScripts'
  | 'fingerprint'
  | 'input'

const AGENT_SCRIPT_SLOTS = ['preprocess', 'finalize', 'helpers', 'modules'] as const

/** Which tabs have something to say, read off the builder's JSON-path diff. */
export function changedSections(diff?: TabularConfigDraftDiff): Set<ChangedSection> {
  const sections = new Set<ChangedSection>()
  for (const entry of diff?.entries ?? []) {
    const section = sectionOf(entry.path)
    if (section) sections.add(section)
  }
  return sections
}

/**
 * What an update draft changed in the contract that no person wrote: the
 * tables and/or checks, in words, or nothing. A `contract_edit` draft only
 * implements the contract the person typed, and a `description_edit` draft
 * only the column hints they typed, so neither warns. Page tables are how the
 * parser reads, not what it promises, so they never count.
 */
export function builderContractChange(
  mode: 'build' | 'update',
  reasonKind: string,
  sections: Iterable<ChangedSection>
): 'tables' | 'checks' | 'tables and checks' | undefined {
  if (mode !== 'update' || reasonKind === 'contract_edit' || reasonKind === 'description_edit') {
    return undefined
  }
  const changed = new Set(sections)
  const tables = changed.has('output')
  const checks = changed.has('checks')
  if (tables && checks) return 'tables and checks'
  if (tables) return 'tables'
  if (checks) return 'checks'
  return undefined
}

function sectionOf(path: string): ChangedSection | undefined {
  if (path === 'output' || path.startsWith('output.') || path.startsWith('output[')) return 'output'
  if (path === 'pageTables' || path.startsWith('pageTables[')) return 'pageTables'
  if (path === 'checks' || path.startsWith('checks[') || path.startsWith('checks.')) return 'checks'
  if (path === 'description') return 'description'
  if (path === 'parser.script' || path === 'parser.entrypoint') return 'script'
  if (AGENT_SCRIPT_SLOTS.some((slot) => isUnder(path, `parser.${slot}`))) return 'agentScripts'
  if (path === 'parser.fingerprint' || path.startsWith('parser.fingerprint.')) return 'fingerprint'
  if (path === 'input' || path.startsWith('input.') || path === 'maxAgentTurns') return 'input'
  return undefined
}

function isUnder(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}.`) || path.startsWith(`${prefix}[`)
}

export interface FingerprintCellChange {
  sheet: string
  /** Zero-based index into the sheet's `cells`. */
  cell: number
  before?: string
  after?: string
}

const WORKBOOK_CELL_RE = /^parser\.fingerprint\.headers\.(.+)\.cells\[(\d+)\]$/
const CSV_CELL_RE = /^parser\.fingerprint\.header\[(\d+)\]$/

/**
 * Fingerprint changes arrive one entry per cell
 * (`parser.fingerprint.headers.<sheet>.cells[2]`, or `parser.fingerprint.header[2]`
 * for CSV). Sheet names may contain dots, so the sheet is whatever sits between
 * `headers.` and the last `.cells[`.
 */
export function fingerprintCellChanges(diff?: TabularConfigDraftDiff): FingerprintCellChange[] {
  const changes: FingerprintCellChange[] = []
  for (const entry of diff?.entries ?? []) {
    const workbook = WORKBOOK_CELL_RE.exec(entry.path)
    if (workbook) {
      changes.push({
        sheet: workbook[1]!,
        cell: Number(workbook[2]),
        ...(typeof entry.before === 'string' ? { before: entry.before } : {}),
        ...(typeof entry.after === 'string' ? { after: entry.after } : {})
      })
      continue
    }
    const csv = CSV_CELL_RE.exec(entry.path)
    if (csv) {
      changes.push({
        sheet: 'header',
        cell: Number(csv[1]),
        ...(typeof entry.before === 'string' ? { before: entry.before } : {}),
        ...(typeof entry.after === 'string' ? { after: entry.after } : {})
      })
    }
  }
  return changes
}

/** The one `parser.script` entry, when the diff has it. */
export function scriptChange(
  diff?: TabularConfigDraftDiff
): { before: string; after: string } | undefined {
  const entry = diff?.entries.find((candidate) => candidate.path === 'parser.script')
  if (!entry) return undefined
  return {
    before: typeof entry.before === 'string' ? entry.before : '',
    after: typeof entry.after === 'string' ? entry.after : ''
  }
}
